import { useEffect } from "react";

// Genuine text surfaces where the native menu (paste / spellcheck / look-up)
// still belongs — everything else in the shell is platform chrome.
const TEXT_SELECTOR =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"]), .arch-selectable, .cm-editor, .monaco-editor';

/**
 * Platform takeover of right-click. ArchLang's board is an application, not a
 * webpage: inside the `.arch-shell` frame the native browser context menu never
 * appears. Surfaces with a custom menu open it (their own handler runs on the
 * bubble phase and calls preventDefault); every other surface has the native
 * menu suppressed here rather than falling through to the browser. Genuine text
 * (inputs, code editors, prose marked `.arch-selectable`) keeps the native menu.
 *
 * This is the complement to `useContextMenu` — that gives a surface a menu;
 * this guarantees no surface leaks the browser's menu. Mount once at the shell.
 */
export function useNativeContextMenuSuppression(): void {
  useEffect(() => {
    const onContextMenu = (e: MouseEvent): void => {
      // Another handler already owned it (a custom menu called preventDefault).
      if (e.defaultPrevented) return;
      const t = e.target as Element | null;
      if (!t || typeof t.closest !== "function") return;
      if (!t.closest(".arch-shell")) return; // only inside the app shell
      if (t.closest(TEXT_SELECTOR)) return; // genuine text keeps the native menu
      e.preventDefault();
    };
    window.addEventListener("contextmenu", onContextMenu);
    return () => window.removeEventListener("contextmenu", onContextMenu);
  }, []);
}
